import {
  Error as ErrorIcon,
  Flash,
  Light,
  VoiceActivate,
} from '@carbon/icons-react';
import { FlagNames } from '@flags/index';
import { useFlag } from '@flags/use-flag';
import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { BackgroundNoiseTester } from '../../components/interview-setup/background-noise-tester';
import { CameraSelector } from '../../components/interview-setup/camera-selector';
import { InternetSpeedTester } from '../../components/interview-setup/internet-speed-tester';
import { MicrophoneSelector } from '../../components/interview-setup/microphone-selector';
import { MicrophoneTester } from '../../components/interview-setup/microphone-tester';
import { ScreenShareTester } from '../../components/interview-setup/screen-share-tester';
import { SpeakerSelector } from '../../components/interview-setup/speaker-selector';
import { SpeakerTester } from '../../components/interview-setup/speaker-tester';
import { UnsupportedBrowserWarning } from '../../components/interview-setup/unsupported-browser-warning';
import { CameraPreview } from '../../components/interview-setup/video-preview';
import { ZaraPreview } from '../../components/interview-setup/zara-preview';

const MICROPHONE_TEST_THRESHOLD = -60.0; // dB
const BACKGROUND_NOISE_TEST_THRESHOLD = -60.0; // dB

enum InterviewSetupStep {
  Camera = 0,
  Speaker = 1,
  Microphone = 2,
  BackgroundNoise = 3,
  ScreenShare = 4,
}

type InterviewSetupProps = {
  isStarted: boolean;
  duration: number;
  onRequestStart: () => void;
  isWaitingForStart: boolean;
  isLiveCoding?: boolean;
};

const commonBoxStyles = {
  height: '100%',
  backgroundColor: 'background.paper',
  padding: 2,
  display: 'flex',
  flexDirection: 'column',
};

const InterviewSetup = ({
  isStarted,
  onRequestStart,
  isWaitingForStart,
  isLiveCoding = false,
}: InterviewSetupProps) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(InterviewSetupStep.Camera);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const shouldUseInternetSpeedTest = useFlag(FlagNames.UseInternetSpeedTester);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
    setIsNextDisabled(true);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setIsNextDisabled(true);
  };

  const onMicrophoneReady = useCallback(
    (ready: boolean) => {
      if (activeStep === InterviewSetupStep.Microphone) {
        setIsNextDisabled(!ready);
      }
    },
    [activeStep],
  );

  const onBackgroundNoiseReady = useCallback(() => {
    if (activeStep === InterviewSetupStep.BackgroundNoise) {
      setIsNextDisabled(false);
    }
  }, [activeStep]);

  const onScreenShareReady = useCallback(
    (ready: boolean) => {
      if (activeStep === InterviewSetupStep.ScreenShare) {
        setIsNextDisabled(!ready);
      }
    },
    [activeStep],
  );

  useEffect(() => {
    if (
      activeStep === InterviewSetupStep.Camera ||
      activeStep === InterviewSetupStep.Speaker
    ) {
      setIsNextDisabled(false);
      return;
    }
  }, [activeStep]);

  const getButtonText = (activeStep: number) => {
    switch (activeStep) {
      case InterviewSetupStep.Camera:
        return 'Test sound';
      case InterviewSetupStep.Speaker:
        return 'Test microphone';
      case InterviewSetupStep.Microphone:
        return isLiveCoding ? 'Test background noise' : 'Test background noise';
      case InterviewSetupStep.BackgroundNoise:
        return isLiveCoding ? 'Set up screen sharing' : undefined;
      case InterviewSetupStep.ScreenShare:
        return 'Begin interview';
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case InterviewSetupStep.Camera:
        return (
          <Box>
            <Typography variant='subtitle2' sx={{ mt: 2 }}>
              Tips for the interview:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                my: 1,
              }}
            >
              {!shouldUseInternetSpeedTest && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Flash />
                  <Typography variant='body2'>
                    Fast internet connection
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <VoiceActivate />
                <Typography variant='body2'>
                  Quiet room with no background noise
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Light />
                <Typography variant='body2'>
                  Good lighting conditions to see your face
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ErrorIcon />
                <Typography variant='body2' sx={{ color: '#f87171' }}>
                  AI use or outside human assistance will result in a ban
                </Typography>
              </Box>
              {shouldUseInternetSpeedTest && <InternetSpeedTester />}
            </Box>
          </Box>
        );
      case InterviewSetupStep.Speaker:
        return (
          <>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <SpeakerTester />
              <Typography variant='body2' gutterBottom>
                Please use headphones or earphones to prevent audio feedback.
                Select your audio output device from the dropdown under the
                camera preview and click the test button to play a sound. A
                sphere will animate in response to the audio to confirm
                it&apos;s working correctly.
              </Typography>
            </Box>
          </>
        );
      case InterviewSetupStep.Microphone:
        return (
          <MicrophoneTester
            noiseThreshold={MICROPHONE_TEST_THRESHOLD}
            onMicrophoneReady={onMicrophoneReady}
          />
        );
      case InterviewSetupStep.BackgroundNoise:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                marginTop: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <BackgroundNoiseTester
                  noiseThreshold={BACKGROUND_NOISE_TEST_THRESHOLD}
                  onBackgroundNoiseReady={onBackgroundNoiseReady}
                />
              </Box>
            </Box>
          </Box>
        );
      case InterviewSetupStep.ScreenShare:
        return <ScreenShareTester onScreenShareReady={onScreenShareReady} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '90vh' },
      }}
    >
      <Box
        sx={{
          width: { sm: 1200, md: 'md', lg: 'lg' },
          maxWidth: { xs: 340, sm: 600, md: 'md', lg: 'lg' },
          padding: { xs: 2, md: 4 },
        }}
      >
        <UnsupportedBrowserWarning />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant='h6'>Assessment with our AI, Zara</Typography>
          <Typography variant='body1' gutterBottom>
            Zara will conduct an AI-powered interview to capture your unique
            skills and expertise. Your interview will be recorded and may be
            reviewed by a human expert.
          </Typography>
        </Box>

        <Grid
          container
          sx={{
            border: 1,
            borderColor: theme.palette.divider,
            borderRadius: 2,
            overflow: 'hidden',
            marginBottom: 10,
            height: '100%',
          }}
        >
          <Grid item xs={12} md={7}>
            <Box
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={commonBoxStyles}>
                  <Typography variant='h6' gutterBottom>
                    Check your camera
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    Please ensure you are in a well lit room and that your face
                    is clearly visible in the center of the frame.
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      marginTop: 2,
                    }}
                  >
                    <Box sx={{ mt: 2 }}>
                      <CameraPreview />
                    </Box>
                    <Box
                      sx={{
                        mt: 3,
                        display: 'flex',
                        gap: 2,
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                      }}
                    >
                      <CameraSelector />
                      <MicrophoneSelector />
                      <SpeakerSelector />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5} sx={{ minHeight: { xs: 630, lg: 570 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.default,
                padding: 3,
                height: '100%',
                paddingBottom: 3,
                borderLeft: { xs: 0, md: 1 },
                borderTop: { xs: 1, md: 0 },
                borderColor: {
                  xs: theme.palette.divider,
                  md: theme.palette.divider,
                },
                gap: 1,
              }}
            >
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{
                  fontSize: '1.5rem',
                }}
              >
                {isStarted
                  ? 'Continue interview with Zara'
                  : 'Ready to interview with Zara?'}
              </Typography>
              {activeStep !== InterviewSetupStep.Microphone && (
                <Box
                  sx={{ ...commonBoxStyles, borderRadius: 2, maxHeight: 240 }}
                >
                  <ZaraPreview />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  justifyContent: 'space-between',
                }}
              >
                {renderStepContent(activeStep)}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    justifySelf: 'flex-end',
                    marginBottom: { sm: 2, lg: 0 },
                  }}
                >
                  <Button
                    disabled={activeStep === InterviewSetupStep.Camera}
                    onClick={handleBack}
                    variant='outlined'
                  >
                    Back
                  </Button>
                  {(activeStep === InterviewSetupStep.BackgroundNoise &&
                    !isLiveCoding) ||
                  (activeStep === InterviewSetupStep.ScreenShare &&
                    isLiveCoding) ? (
                    <Button
                      variant='contained'
                      onClick={onRequestStart}
                      color='primary'
                      disabled={isWaitingForStart || isNextDisabled}
                    >
                      {isWaitingForStart
                        ? 'Waiting to start...'
                        : 'Begin interview'}
                    </Button>
                  ) : (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleNext}
                      disabled={isNextDisabled}
                    >
                      {getButtonText(activeStep)}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default InterviewSetup;
